import { AppliedCouponType, useCheckoutContext } from "lib/contexts/CheckoutProvider";
import React, { useState } from "react";
import { Gift } from "@phosphor-icons/react";
import { useLocale } from "lib/hooks/useLocale";
import { Icon } from "@shopflo/ui/icon";
import { CouponPageConfigType } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "@shopflo/ui/utils";

interface ProductSelectorCardProps {
  coupon: AppliedCouponType;
  customisations?: CouponPageConfigType;
}

const ProductSelectorCard: React.FC<ProductSelectorCardProps> = React.memo(({ coupon, customisations }) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const {
    actions: { setDiscountProductSelectorModal },
  } = useCheckoutContext();
  const { t } = useLocale();

  return (
    <div
      className="group relative box-border flex h-full w-full flex-col items-start overflow-hidden rounded-2xl border border-zinc-200 pb-3"
      style={{ borderRadius: customisations?.cardRadius }}
    >
      <div
        className="flex w-full flex-row items-center justify-between py-2 pl-3 pr-2"
        style={{
          background: `linear-gradient(to bottom, ${hexToRgba(customisations?.primaryColor ?? '#000000', 0.2)}, transparent)`,
        }}
      >
        <div className="flex flex-row items-center gap-1 w-3/4">
          <div className="flex items-center justify-start">
            <Icon
              IconComponent={Gift}
              size="sm"
              style={{ color: customisations?.primaryColor ?? undefined }}
            />
          </div>
          <h3 className="truncate pr-2 text-text-sm-semibold uppercase">
            {coupon.title ? coupon.title : coupon.code}
          </h3>
        </div>
        <button
          type="button"
          className="flex items-center justify-center text-text-xs-semibold px-2.5 py-1 text-white"
          style={{
            backgroundColor: customisations?.primaryColor,
            borderRadius: customisations?.buttonRadius,
            color: customisations?.textColor,
          }}
          onClick={() => setDiscountProductSelectorModal("DISCOUNTED_PRODUCT_SELECTOR")}
        >
          {t("view")}
        </button>
      </div>

      <div className="group relative flex w-full flex-col items-start justify-between gap-2 overflow-x-hidden px-3">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <h3 className="text-text-xs-semibold" style={{ color: 'var(--flo-coal-dark-color)' }}>
              {coupon.rewardData?.header}
            </h3>
            {coupon.rewardData?.description && (
              <p className="text-text-xs-medium" style={{ color: 'var(--flo-coal-light-color)' }}>
                {coupon.rewardData.description}
              </p>
            )}
          </div>
        </div>

        {coupon.rewardData?.longDescription && coupon.rewardData.longDescription.length > 0 && (
          showMore ? (
            <>
              <ul className="flex w-full flex-col space-y-1 rounded-lg bg-gray-lighter px-2 py-3">
                {coupon.rewardData.longDescription.map((point: string, index: number) => (
                  <li
                    className="ml-4 list-disc text-xs font-normal text-coal-light"
                    key={`${coupon.code}--${index}`}
                  >
                    <p>{point}</p>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                className="w-fit text-text-xxs-medium outline-none"
                style={{ color: 'var(--flo-coal-dark-color)' }}
              >
                Hide details
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="w-fit text-text-xxs-medium outline-none text-zinc-500"
            >
              {t("more")}
            </button>
          )
        )}
      </div>
    </div>
  );
});

export default ProductSelectorCard
